import {
  ApplicationConfig,
  ErrorHandler,
  importProvidersFrom,
  isDevMode,
  LOCALE_ID,
  provideZoneChangeDetection
} from "@angular/core";
import { provideRouter, TitleStrategy } from "@angular/router";

import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptors,
  withInterceptorsFromDi
} from "@angular/common/http";
import { MAT_CARD_CONFIG } from "@angular/material/card";
import { provideNativeDateAdapter } from "@angular/material/core";
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from "@angular/material/form-field";
import {
  getBrowserCultureLang,
  provideTransloco,
  TranslocoService
} from "@jsverse/transloco";
import { provideTranslocoLocale } from "@jsverse/transloco-locale";
import { routes } from "./app.routes";
import { AuthInterceptor } from "./core/interceptors/auth/auth.interceptor";
import { loaderInterceptor } from "./core/interceptors/loader/loader.interceptor";
import { SnackbarNotificationModule } from "./core/modules/snackbar-notification/snackbar-notification.module";
import { GlobalErrorHandlerService } from "./core/services/global-error-handler/global-error-handler.service";
import { AppTitleService } from "./core/services/page-title-resolver/app-title.service";
import { LabelLang } from "./shared/types/languages.type";
import { TranslocoHttpLoader } from "./transloco-loader";

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    {
      provide: ErrorHandler,
      useClass: GlobalErrorHandlerService
    },
    provideRouter(routes),
    provideHttpClient(
      withInterceptors([loaderInterceptor]),
      withInterceptorsFromDi()
    ),
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    provideTransloco({
      config: {
        availableLangs: Object.values(LabelLang),
        defaultLang: getDefaultLanguage(),
        reRenderOnLangChange: true,
        prodMode: !isDevMode()
      },
      loader: TranslocoHttpLoader
    }),
    provideTranslocoLocale(),
    {
      provide: LOCALE_ID,
      useFactory: (translocoService: TranslocoService) =>
        translocoService.getActiveLang(),
      deps: [TranslocoService]
    },
    importProvidersFrom(SnackbarNotificationModule.forRoot()),
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: { appearance: "outline" }
    },
    {
      provide: MAT_CARD_CONFIG,
      useValue: { appearance: "outlined" }
    },
    provideNativeDateAdapter(),
    { provide: TitleStrategy, useClass: AppTitleService }
  ]
};

function getDefaultLanguage(): LabelLang {
  const browserCultureLang = getBrowserCultureLang() as LabelLang;
  if (Object.values(LabelLang).includes(browserCultureLang)) {
    return browserCultureLang;
  }

  // fallback
  return LabelLang.IT_IT;
}
